import { useState, useCallback, useRef } from 'react';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';

/**
 * usePdfExport - Hook to capture a DOM element and export as PDF
 * 
 * @param {Object} options
 * @param {string} options.filename - Base filename for the PDF (without extension)
 * @param {number} options.scale - Scale factor for image quality (default: 3 for retina)
 * @param {string} options.bgColor - Background color for the capture (default: '#ffffff')
 * @param {string} options.format - 'a4' | 'letter' (default: 'a4')
 * @param {string} options.orientation - 'portrait' | 'landscape' (default: 'portrait')
 * @returns {{ exporting, exportPdf, PdfButton }}
 */
const usePdfExport = ({
  filename = 'report',
  scale = 3,
  bgColor = '#ffffff',
  format = 'a4',
  orientation = 'portrait',
} = {}) => {
  const [exporting, setExporting] = useState(false);
  const contentRef = useRef(null);

  const exportPdf = useCallback(async (customFilename) => {
    if (!contentRef.current) return;

    setExporting(true);
    try {
      // Capture the DOM element as a high-res PNG
      const dataUrl = await toPng(contentRef.current, {
        quality: 1,
        pixelRatio: scale,
        backgroundColor: bgColor,
        cacheBust: true,
      });

      // PDF dimensions in mm
      const pdfFormat = format === 'letter' ? [215.9, 279.4] : [210, 297];
      const [width, height] = pdfFormat;
      const useWidth = orientation === 'landscape' ? height : width;
      const useHeight = orientation === 'landscape' ? width : height;

      // Calculate image dimensions to fit PDF with margins
      const margin = 15; // mm
      const maxWidth = useWidth - margin * 2;
      const maxHeight = useHeight - margin * 2;

      // Create a temporary image to get natural dimensions
      const img = new Image();
      img.src = dataUrl;
      await new Promise((resolve) => { img.onload = resolve; });

      let imgWidth = maxWidth;
      let imgHeight = (img.naturalHeight / img.naturalWidth) * imgWidth;

      // If image is taller than page, scale down
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = (img.naturalWidth / img.naturalHeight) * imgHeight;
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: pdfFormat,
      });

      // Add title
      const title = customFilename || filename;
      pdf.setFontSize(16);
      pdf.setTextColor(30, 41, 59);
      pdf.text(title.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), margin, 20);

      // Add date
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
      pdf.text(`Generated: ${dateStr}`, margin, 27);

      // Add separator line
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 31, useWidth - margin, 31);

      // Calculate image position (centered below title area)
      const yOffset = 38;
      const xOffset = (useWidth - imgWidth) / 2;

      pdf.addImage(dataUrl, 'PNG', xOffset, yOffset, imgWidth, imgHeight);

      // Add footer
      pdf.setFontSize(8);
      pdf.setTextColor(156, 163, 175);
      pdf.text(`Page 1 of 1`, useWidth / 2, useHeight - 10, { align: 'center' });

      // Save
      const safeName = (customFilename || filename).toLowerCase().replace(/[^a-z0-9]+/g, '-');
      pdf.save(`${safeName}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [filename, scale, bgColor, format, orientation]);

  return {
    exporting,
    exportPdf,
    contentRef,
  };
};

export default usePdfExport;
